import React, { useEffect, useMemo, useRef, useState } from 'react';

import ReceiptPreview from './components/ReceiptPreview';
import InvoicePreview from './components/InvoicePreview';
import PDFExportButton from './components/PDFExportButton';

import {
  templates,
  loadTemplate as loadReceiptTemplate
} from './templates';

import {
  invoiceTemplates,
  loadInvoiceTemplate
} from './InvoiceTemplates';

import './styles/index.css';
import './styles/futuristic-theme.css';

const emptyData = {};

const faqItems = [
  {
    question: "Qu'est-ce que Resell Addict ?",
    answer:
      "Resell Addict est un générateur accessible depuis le navigateur pour créer et personnaliser des tickets de caisse et des factures, puis les exporter en PDF."
  },
  {
    question: "Comment accéder au générateur ?",
    answer:
      "Un accès est nécessaire. Entre l'adresse e-mail associée à ton accès ainsi que le code reçu après ton achat, puis valide pour accéder aux services."
  },
  {
    question: "Combien de temps mon accès est-il valable ?",
    answer:
      "La durée dépend du code qui t'a été attribué. Il peut être valable pour une durée limitée ou être illimité. La page Services affiche automatiquement le temps restant."
  },
  {
    question: "Puis-je réutiliser mon accès plus tard ?",
    answer:
      "Oui. Une fois connecté, ton accès est mémorisé dans le même navigateur tant qu'il est encore valide. Tu n'as donc pas besoin de saisir à nouveau ton code à chaque visite."
  },
  {
    question: "Quels services sont disponibles ?",
    answer:
      "Deux services sont actuellement disponibles : le générateur de ticket de caisse et le générateur de factures."
  },
  {
    question: "Puis-je modifier les informations du document ?",
    answer:
      "Oui. Les éléments éditables du document peuvent être modifiés directement dans l'aperçu du générateur."
  },
  {
    question: "Puis-je exporter mon document ?",
    answer:
      "Oui. Une fois ton document prêt, tu peux l'exporter au format PDF depuis le générateur."
  },
  {
    question: "J'ai un problème avec mon accès, que faire ?",
    answer:
      "Vérifie d'abord l'adresse e-mail et le code utilisés. Si le problème continue, tu peux contacter le Staff Resell Addict via le serveur Discord."
  }
];


async function buildTemplateDeploymentId(templatesByService) {
  const payload = JSON.stringify({
    ticket: [...templatesByService.ticket].sort(),
    invoice: [...templatesByService.invoice].sort()
  });

  if (window.crypto?.subtle) {
    const bytes = new TextEncoder().encode(payload);
    const digest = await window.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  return payload;
}

function App() {
  const [page, setPage] = useState('home');
  const [siteLoading, setSiteLoading] = useState(true);
  const [accessValidated, setAccessValidated] = useState(false);
  const [accessClosing, setAccessClosing] = useState(false);
  const [accessSuccessClosing, setAccessSuccessClosing] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  // =========================
  // CHARGEMENT INITIAL
  // =========================
  useEffect(() => {
    const timer = window.setTimeout(() => setSiteLoading(false), 1100);
    return () => window.clearTimeout(timer);
  }, []);


  // =========================
  // SESSION WATCHDOG
  // =========================
  useEffect(() => {
    let stopped = false;
    let checking = false;

    const forceRelogin = () => {
      localStorage.removeItem('resell_addict_access');
      sessionStorage.removeItem('resell_addict_access');

      setAccessStep('credentials');
      setVerificationCode('');
      setVerificationEmail('');
      setVerificationError('');
      setAccessEmail('');
      setAccessCode('');
      setAccessLoading(false);
      setAccessError(
        'Cette session a été remplacée par une nouvelle connexion. Veuillez confirmer votre accès par e-mail.'
      );

      setAccessOpen(true);
      setAccessClosing(false);
      setAccessValidated(false);
      setAccessSuccessClosing(false);
      setPage('home');
    };

    const checkSession = async () => {
      if (stopped || checking || !navigator.onLine) {
        return;
      }

      const stored =
        localStorage.getItem('resell_addict_access') ||
        sessionStorage.getItem('resell_addict_access');

      if (!stored) {
        return;
      }

      let access;
      try {
        access = JSON.parse(stored);
      } catch {
        return;
      }

      if (
        !access?.email ||
        !access?.expiresAt ||
        Date.now() >= Number(access.expiresAt)
      ) {
        return;
      }

      checking = true;

      try {
        const response = await fetch(
          '/api/access/session',
          {
            method: 'GET',
            credentials: 'include',
            cache: 'no-store'
          }
        );

        if (!response.ok) {
          forceRelogin();
        }
      } catch {
        // Une panne réseau temporaire ne déconnecte pas l'utilisateur.
      } finally {
        checking = false;
      }
    };

    checkSession();

    const intervalId =
      window.setInterval(checkSession, 2000);

    window.addEventListener(
      'focus',
      checkSession
    );

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'visible') {
          checkSession();
        }
      }
    );

    return () => {
      stopped = true;
      window.clearInterval(intervalId);
      window.removeEventListener(
        'focus',
        checkSession
      );
    };
  }, []);

  // =========================
  // SERVICES
  // =========================
  const [selectedService, setSelectedService] = useState(null);
  const [serviceOpening, setServiceOpening] = useState(false);
  const [generatorEntering, setGeneratorEntering] = useState(false);

  // Transition générique pour les changements de page.
  const [pageNavigating, setPageNavigating] = useState(false);

  const navigateWithTransition = (
    nextPage,
    delay = 220
  ) => {
    if (pageNavigating) {
      return;
    }

    setPageNavigating(true);

    window.setTimeout(() => {
      setPage(nextPage);

      window.requestAnimationFrame(() => {
        setPageNavigating(false);
      });
    }, delay);
  };

  // =========================
  // TICKET TEMPLATES
  // =========================
  const [templateMenuOpen, setTemplateMenuOpen] = useState(false);
  const [templateAnimating, setTemplateAnimating] = useState(false);

  const templateMenuRef = useRef(null);
  const previewRef = useRef(null);

  const templateIds = useMemo(
    () => Object.keys(templates),
    []
  );

  const [selectedTemplate, setSelectedTemplate] = useState(
    templateIds[0] || 'hermes'
  );

  const [formData, setFormData] = useState(emptyData);

  const templateMeta =
    templates[selectedTemplate];

  const [template, setTemplate] =
    useState(null);

  // =========================
  // INVOICE TEMPLATES
  // =========================
  const [invoiceMenuOpen, setInvoiceMenuOpen] = useState(false);
  const [invoiceAnimating, setInvoiceAnimating] = useState(false);

  const invoiceMenuRef = useRef(null);
  const invoicePreviewRef = useRef(null);

  const invoiceTemplateIds = useMemo(
    () => Object.keys(invoiceTemplates),
    []
  );

  const [selectedInvoiceTemplate, setSelectedInvoiceTemplate] = useState(
    invoiceTemplateIds[0] || 'apple'
  );

  const [invoiceFormData, setInvoiceFormData] =
    useState(emptyData);

  const invoiceTemplateMeta =
    invoiceTemplates[
      selectedInvoiceTemplate
    ];

  const [invoiceTemplate, setInvoiceTemplate] =
    useState(null);

  // =========================
  // NOUVEAUX MODÈLES / MODÈLES VUS
  // =========================
  // Les nouveaux modèles sont détectés automatiquement par le serveur.
  // Un modèle est nouveau dès qu'il apparaît pour la première fois dans
  // le catalogue global. Chaque compte conserve ensuite ses propres vues.
  const [seenTemplateIds, setSeenTemplateIds] = useState({
    ticket: [],
    invoice: []
  });

  const [newTemplateIds, setNewTemplateIds] = useState({
    ticket: [],
    invoice: []
  });

  const isTemplateNewForCurrentUser = (service, id) =>
    newTemplateIds[service]?.includes(id) &&
    !seenTemplateIds[service]?.includes(id);

  const getNewTemplateCount = (service, ids) =>
    ids.reduce(
      (count, id) =>
        count +
        (isTemplateNewForCurrentUser(service, id) ? 1 : 0),
      0
    );

  // =========================
  // SUGGESTION DE MARQUE
  // =========================
  const [brandSuggestionOpen, setBrandSuggestionOpen] = useState(false);
  const [brandSuggestionName, setBrandSuggestionName] = useState('');
  const [brandSuggestionService, setBrandSuggestionService] = useState('ticket');
  const [brandSuggestionSending, setBrandSuggestionSending] = useState(false);
  const [brandSuggestionMessage, setBrandSuggestionMessage] = useState('');
  const [brandSuggestionIsError, setBrandSuggestionIsError] = useState(false);

  // =========================
  // SIGNALER UN PROBLÈME
  // =========================
  const [problemReportOpen, setProblemReportOpen] = useState(false);
  const [problemReportService, setProblemReportService] = useState('ticket');
  const [problemReportText, setProblemReportText] = useState('');
  const [problemReportFile, setProblemReportFile] = useState(null);
  const [problemReportSending, setProblemReportSending] = useState(false);
  const [problemReportMessage, setProblemReportMessage] = useState('');
  const [problemReportIsError, setProblemReportIsError] = useState(false);

  // =========================
  // ACCESS
  // =========================
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessEmail, setAccessEmail] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [accessError, setAccessError] = useState('');
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessStep, setAccessStep] = useState('credentials');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [verificationEmail, setVerificationEmail] = useState('');

  // =========================
  // CHARGEMENT ASYNCHRONE DES TEMPLATES
  // =========================
  useEffect(() => {
    let cancelled = false;

    setTemplate(null);

    loadReceiptTemplate(
      selectedTemplate
    )
      .then((loaded) => {
        if (!cancelled) {
          setTemplate(loaded);
        }
      })
      .catch((error) => {
        console.error(
          'Erreur de chargement du template ticket :',
          error
        );
      });

    return () => {
      cancelled = true;
    };
  }, [selectedTemplate]);

  useEffect(() => {
    let cancelled = false;

    setInvoiceTemplate(null);

    loadInvoiceTemplate(
      selectedInvoiceTemplate
    )
      .then((loaded) => {
        if (!cancelled) {
          setInvoiceTemplate(loaded);
        }
      })
      .catch((error) => {
        console.error(
          'Erreur de chargement du template facture :',
          error
        );
      });

    return () => {
      cancelled = true;
    };
  }, [selectedInvoiceTemplate]);

  // =========================
  // CLOSE DROPDOWNS OUTSIDE
  // =========================
  useEffect(() => {
    const handleOutside = (event) => {
      if (
        templateMenuRef.current &&
        !templateMenuRef.current.contains(event.target)
      ) {
        setTemplateMenuOpen(false);
      }

      if (
        invoiceMenuRef.current &&
        !invoiceMenuRef.current.contains(event.target)
      ) {
        setInvoiceMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);

    return () => {
      document.removeEventListener('mousedown', handleOutside);
    };
  }, []);

  // =========================
  // TICKET TEMPLATE CHANGE
  // =========================
  const handleTemplateChange = (id) => {
    void markTemplateAsSeen('ticket', id);

    if (id === selectedTemplate) {
      setTemplateMenuOpen(false);
      return;
    }

    setTemplateMenuOpen(false);

    setTemplateAnimating(false);

    requestAnimationFrame(() => {
      setTemplateAnimating(true);
    });

    setSelectedTemplate(id);
    setFormData({});

    window.setTimeout(() => {
      setTemplateAnimating(false);
    }, 620);
  };

  // =========================
  // INVOICE TEMPLATE CHANGE
  // =========================
  const handleInvoiceTemplateChange = (id) => {
    void markTemplateAsSeen('invoice', id);

    if (id === selectedInvoiceTemplate) {
      setInvoiceMenuOpen(false);
      return;
    }

    setInvoiceMenuOpen(false);

    setInvoiceAnimating(false);

    requestAnimationFrame(() => {
      setInvoiceAnimating(true);
    });

    setSelectedInvoiceTemplate(id);
    setInvoiceFormData({});

    window.setTimeout(() => {
      setInvoiceAnimating(false);
    }, 620);
  };

  // =========================
  // SUGGESTION DE MARQUE
  // =========================
  const openBrandSuggestion = (service) => {
    setTemplateMenuOpen(false);
    setInvoiceMenuOpen(false);
    setBrandSuggestionService(service);
    setBrandSuggestionName('');
    setBrandSuggestionMessage('');
    setBrandSuggestionIsError(false);
    setBrandSuggestionSending(false);
    setBrandSuggestionOpen(true);
  };

  const closeBrandSuggestion = () => {
    if (brandSuggestionSending) {
      return;
    }

    setBrandSuggestionOpen(false);
    setBrandSuggestionMessage('');
    setBrandSuggestionIsError(false);
  };

  const getSuggestedCurrentTemplate = () => {
    if (brandSuggestionService === 'invoice') {
      return (
        invoiceTemplateMeta?.name ||
        selectedInvoiceTemplate ||
        'Facture'
      );
    }

    return (
      templateMeta?.name ||
      selectedTemplate ||
      'Ticket'
    );
  };

  const submitBrandSuggestion = async (event) => {
    event.preventDefault();

    const brand = brandSuggestionName.trim();

    if (brand.length < 2) {
      setBrandSuggestionIsError(true);
      setBrandSuggestionMessage(
        'Entrez un nom de marque valide.'
      );
      return;
    }

    setBrandSuggestionSending(true);
    setBrandSuggestionMessage('');
    setBrandSuggestionIsError(false);

    try {
      const response = await fetch(
        '/api/suggest-brand',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            brand,
            service:
              brandSuggestionService === 'invoice'
                ? 'Facture'
                : 'Ticket',
            currentTemplate:
              getSuggestedCurrentTemplate()
          })
        }
      );

      const result = await response
        .json()
        .catch(() => ({}));

      if (!response.ok || !result.success) {
        throw new Error(
          result.error ||
          'Impossible d\u2019envoyer la suggestion.'
        );
      }

      setBrandSuggestionSending(false);
      setBrandSuggestionIsError(false);
      setBrandSuggestionMessage(
        'Suggestion envoy\u00e9e. Merci !'
      );

      window.setTimeout(() => {
        setBrandSuggestionOpen(false);
        setBrandSuggestionName('');
        setBrandSuggestionMessage('');
      }, 1100);
    } catch (error) {
      setBrandSuggestionSending(false);
      setBrandSuggestionIsError(true);
      setBrandSuggestionMessage(
        error?.message ||
        'Erreur lors de l\u2019envoi.'
      );
    }
  };


  const getProblemReportCurrentTemplate = () => {
    if (problemReportService === 'invoice') {
      return (
        invoiceTemplateMeta?.name ||
        selectedInvoiceTemplate ||
        'Facture'
      );
    }

    return (
      templateMeta?.name ||
      selectedTemplate ||
      'Ticket'
    );
  };

  const openProblemReport = (service) => {
    setTemplateMenuOpen(false);
    setInvoiceMenuOpen(false);
    setProblemReportService(service);
    setProblemReportText('');
    setProblemReportFile(null);
    setProblemReportMessage('');
    setProblemReportIsError(false);
    setProblemReportSending(false);
    setProblemReportOpen(true);
  };

  const closeProblemReport = () => {
    if (problemReportSending) {
      return;
    }

    setProblemReportOpen(false);
    setProblemReportMessage('');
    setProblemReportIsError(false);
    setProblemReportFile(null);
  };

  const handleProblemReportFile = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';

    if (!file) {
      return;
    }

    const maxSize = 8 * 1024 * 1024;
    const allowedTypes = new Set([
      'image/png',
      'image/jpeg',
      'image/webp',
      'image/gif'
    ]);

    if (!allowedTypes.has(file.type)) {
      setProblemReportIsError(true);
      setProblemReportMessage(
        'La pièce jointe doit être une image PNG, JPG, WebP ou GIF.'
      );
      return;
    }

    if (file.size > maxSize) {
      setProblemReportIsError(true);
      setProblemReportMessage(
        'La pièce jointe ne peut pas dépasser 8 Mo.'
      );
      return;
    }

    setProblemReportFile(file);
    setProblemReportMessage('');
    setProblemReportIsError(false);
  };

  const submitProblemReport = async (event) => {
    event.preventDefault();

    const message = problemReportText.trim();

    if (message.length < 5) {
      setProblemReportIsError(true);
      setProblemReportMessage(
        'Décris le problème rencontré avec un peu plus de détails.'
      );
      return;
    }

    setProblemReportSending(true);
    setProblemReportMessage('');
    setProblemReportIsError(false);

    try {
      const body = new FormData();
      body.append('message', message);
      body.append(
        'service',
        problemReportService === 'invoice' ? 'Facture' : 'Ticket'
      );
      body.append('currentTemplate', getProblemReportCurrentTemplate());

      if (problemReportFile) {
        body.append('attachment', problemReportFile, problemReportFile.name);
      }

      const response = await fetch('/api/report-problem', {
        method: 'POST',
        credentials: 'same-origin',
        body
      });

      const result = await response
        .json()
        .catch(() => ({}));

      if (!response.ok || !result.success) {
        throw new Error(
          result.error ||
          'Impossible d’envoyer le signalement.'
        );
      }

      setProblemReportSending(false);
      setProblemReportIsError(false);
      setProblemReportMessage(
        'Signalement envoyé. Merci !'
      );

      window.setTimeout(() => {
        setProblemReportOpen(false);
        setProblemReportText('');
        setProblemReportFile(null);
        setProblemReportMessage('');
      }, 1100);
    } catch (error) {
      setProblemReportSending(false);
      setProblemReportIsError(true);
      setProblemReportMessage(
        error?.message ||
        'Erreur lors de l’envoi.'
      );
    }
  };

  // =========================
  // ACCESS
  // =========================
  const getStoredAccess = () => {
    try {
      const localRaw = localStorage.getItem(
        'resell_addict_access'
      );

      if (localRaw) {
        return JSON.parse(localRaw);
      }

      // Compatibilité avec les anciennes sessions déjà enregistrées.
      const sessionRaw = sessionStorage.getItem(
        'resell_addict_access'
      );

      if (!sessionRaw) {
        return null;
      }

      const access = JSON.parse(sessionRaw);

      if (access?.expiresAt) {
        localStorage.setItem(
          'resell_addict_access',
          JSON.stringify(access)
        );
      }

      return access;
    } catch {
      return null;
    }
  };

  const getAccessDurationLabel = () => {
    try {
      const access = getStoredAccess();

      if (!access?.expiresAt) {
        return '';
      }

      const expiresAt = Number(access.expiresAt);

      if (!Number.isFinite(expiresAt)) {
        return '';
      }

      // Accès "inf" : generate-code.py utilise cette date très éloignée.
      const permanentExpiration = Date.parse(
        '9999-12-31T23:59:59.999Z'
      );

      if (expiresAt >= permanentExpiration - 86400000) {
        return 'Accès illimité';
      }

      const remainingMs = expiresAt - Date.now();

      if (remainingMs <= 0) {
        return 'Accès expiré';
      }

      const remainingDays = Math.ceil(
        remainingMs / (1000 * 60 * 60 * 24)
      );

      if (remainingDays === 1) {
        return 'Votre service prend fin dans 1 jour';
      }

      return `Votre service prend fin dans ${remainingDays} jours`;
    } catch {
      return '';
    }
  };

  const hasActiveAccess = () => {
    try {
      const access = getStoredAccess();

      if (!access?.expiresAt) {
        return false;
      }

      const expiresAt = Number(access.expiresAt);

      if (!Number.isFinite(expiresAt)) {
        return false;
      }

      if (Date.now() >= expiresAt) {
        localStorage.removeItem(
          'resell_addict_access'
        );

        sessionStorage.removeItem(
          'resell_addict_access'
        );

        return false;
      }

      return true;
    } catch {
      return false;
    }
  };

  // =========================
  // SYNCHRONISATION DES MODÈLES VUS / NOUVEAUX
  // =========================
  useEffect(() => {
    if (page !== 'services' && page !== 'editor' && page !== 'invoice-editor') {
      return;
    }

    let cancelled = false;

    const syncTemplates = async () => {
      try {
        const currentTemplates = {
          ticket: templateIds,
          invoice: invoiceTemplateIds
        };

        const deploymentId = await buildTemplateDeploymentId(currentTemplates);

        const response = await fetch('/api/templates/seen', {
          method: 'POST',
          credentials: 'include',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            templates: currentTemplates,
            deploymentId
          })
        });

        if (!response.ok) return;

        const result = await response.json().catch(() => ({}));

        if (!cancelled && result.success) {
          setSeenTemplateIds({
            ticket: Array.isArray(result.seen?.ticket) ? result.seen.ticket : [],
            invoice: Array.isArray(result.seen?.invoice) ? result.seen.invoice : []
          });
          setNewTemplateIds({
            ticket: Array.isArray(result.new?.ticket) ? result.new.ticket : [],
            invoice: Array.isArray(result.new?.invoice) ? result.new.invoice : []
          });
        }
      } catch {
        // Le générateur reste utilisable si le réseau est temporairement indisponible.
      }
    };

    syncTemplates();

    return () => { cancelled = true; };
  }, [page, templateIds, invoiceTemplateIds]);

  const markTemplateAsSeen = async (service, id) => {
    if (!newTemplateIds[service]?.includes(id)) return;

    const currentSeen = seenTemplateIds[service] || [];
    if (currentSeen.includes(id)) return;

    setSeenTemplateIds((current) => ({
      ...current,
      [service]: [...(current[service] || []), id]
    }));

    try {
      const response = await fetch('/api/templates/seen', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service, id })
      });

      if (!response.ok) throw new Error('save failed');
    } catch {
      setSeenTemplateIds((current) => ({
        ...current,
        [service]: (current[service] || []).filter((seenId) => seenId !== id)
      }));
    }
  };

  const requestGeneratorAccess = async () => {
    if (pageNavigating || accessLoading) {
      return;
    }

    setAccessError('');

    try {
      const response = await fetch(
        '/api/access/session',
        {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store'
        }
      );

      const result = await response
        .json()
        .catch(() => ({}));

      if (response.ok && result.valid) {
        const accessData = {
          email: result.email,
          expiresAt: new Date(
            result.expiresAt
          ).getTime()
        };

        localStorage.setItem(
          'resell_addict_access',
          JSON.stringify(accessData)
        );

        sessionStorage.setItem(
          'resell_addict_access',
          JSON.stringify(accessData)
        );

        navigateWithTransition(
          'services',
          220
        );

        return;
      }
    } catch {
      // Si la session ne peut pas être vérifiée,
      // on retombe sur l'écran de connexion.
    }

    setAccessStep('credentials');
    setVerificationCode('');
    setVerificationEmail('');
    setVerificationError('');
    setAccessError('');
    setAccessEmail('');
    setAccessCode('');
    setPageNavigating(true);

    window.setTimeout(() => {
      setAccessOpen(true);

      window.requestAnimationFrame(() => {
        setPageNavigating(false);
      });
    }, 180);
  };

  const verifyAccess = async (event) => {
    event.preventDefault();

    setAccessError('');
    setVerificationError('');
    setAccessLoading(true);

    try {
      const response = await fetch(
        '/api/access/verify',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            email: accessEmail.trim(),
            code: accessCode.trim()
          })
        }
      );

      const result = await response
        .json()
        .catch(() => ({}));

      if (!response.ok || !result.valid) {
        setAccessError(
          result.message ||
          'E-mail ou code d’accès incorrect.'
        );

        return;
      }

      if (result.requiresVerification) {
        setVerificationEmail(
          result.email || accessEmail.trim().toLowerCase()
        );
        setVerificationCode('');
        setVerificationError('');
        setAccessStep('email');

        return;
      }

      const accessData = {
        email: accessEmail
          .trim()
          .toLowerCase(),

        expiresAt: new Date(
          result.expiresAt
        ).getTime()
      };

      localStorage.setItem(
        'resell_addict_access',
        JSON.stringify(accessData)
      );

      sessionStorage.setItem(
        'resell_addict_access',
        JSON.stringify(accessData)
      );

      setAccessOpen(false);
      setAccessError('');
      setAccessStep('credentials');
      setVerificationCode('');
      setVerificationEmail('');
      setVerificationError('');
      setAccessValidated(true);

      window.setTimeout(() => {
        setAccessSuccessClosing(true);

        window.setTimeout(() => {
          setAccessSuccessClosing(false);
          setAccessValidated(false);
          setPage('services');
        }, 260);
      }, 980);
    } catch {
      setAccessError(
        'Impossible de vérifier le code. Vérifiez que le serveur est bien démarré.'
      );
    } finally {
      setAccessLoading(false);
    }
  };

  const verifyEmailCode = async (event) => {
    event.preventDefault();

    setVerificationError('');
    setVerificationLoading(true);

    try {
      const response = await fetch(
        '/api/access/verify-email',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            code: verificationCode.trim()
          })
        }
      );

      const result = await response
        .json()
        .catch(() => ({}));

      if (!response.ok || !result.valid) {
        setVerificationError(
          result.message ||
          'Code de vérification incorrect.'
        );

        return;
      }

      const accessData = {
        email: result.email || verificationEmail,
        expiresAt: new Date(
          result.expiresAt
        ).getTime()
      };

      localStorage.setItem(
        'resell_addict_access',
        JSON.stringify(accessData)
      );

      sessionStorage.setItem(
        'resell_addict_access',
        JSON.stringify(accessData)
      );

      setAccessOpen(false);
      setAccessStep('credentials');
      setVerificationCode('');
      setVerificationEmail('');
      setVerificationError('');
      setAccessValidated(true);

      window.setTimeout(() => {
        setAccessSuccessClosing(true);

        window.setTimeout(() => {
          setAccessSuccessClosing(false);
          setAccessValidated(false);
          setPage('services');
        }, 260);
      }, 980);
    } catch {
      setVerificationError(
        'Impossible de vérifier le code. Réessayez dans quelques instants.'
      );
    } finally {
      setVerificationLoading(false);
    }
  };

  // =========================
  // OPEN TICKET GENERATOR
  // =========================
  const openTicketGenerator = () => {
    if (serviceOpening) {
      return;
    }

    setSelectedService('ticket');
    setServiceOpening(true);

    window.setTimeout(() => {
      setServiceOpening(false);
      setGeneratorEntering(true);
      setPage('editor');

      window.setTimeout(() => {
        setGeneratorEntering(false);
      }, 620);
    }, 180);
  };

  // =========================
  // OPEN INVOICE GENERATOR
  // =========================
  const openInvoiceGenerator = () => {
    if (serviceOpening) {
      return;
    }

    setSelectedService('invoice');
    setServiceOpening(true);

    window.setTimeout(() => {
      setServiceOpening(false);
      setGeneratorEntering(true);
      setPage('invoice-editor');

      window.setTimeout(() => {
        setGeneratorEntering(false);
      }, 620);
    }, 180);
  };

  // =========================
  // CHARGEMENT INITIAL
  // =========================
  if (siteLoading) {
    return (
      <main className="site-loader">
        <div className="site-loader-content">
          <div className="site-loader-mark">RA</div>
          <div className="site-loader-title">RESELL ADDICT</div>
          <div className="site-loader-line"><span /></div>
          <div className="site-loader-label">CHARGEMENT</div>
        </div>
      </main>
    );
  }

  // =========================
  // ACCÈS VALIDÉ
  // =========================
  if (accessValidated) {
    return (
      <main
        className={`access-success-page ${
          accessSuccessClosing
            ? 'access-success-closing'
            : ''
        }`}
      >
        <div className="access-success-card">
          <div className="access-success-icon">✓</div>
          <div className="access-success-eyebrow">ACCÈS PRIVÉ</div>
          <h1>Accès validé</h1>
          <p>Bienvenue sur Resell Addict.</p>
          <div className="access-success-loader"><span /></div>
        </div>
      </main>
    );
  }

  // =========================
  // ACCESS PAGE
  // =========================
  if (accessOpen) {
    const isVerificationStep = accessStep === 'email';

    return (
      <main
        className={`access-page ${
          pageNavigating
            ? 'page-navigating'
            : ''
        } ${
          accessClosing
            ? 'access-closing'
            : ''
        }`}
      >
        <div className="access-card">

          <button
            type="button"
            className="access-close"
            onClick={() => {
              if (accessClosing) {
                return;
              }

              setAccessClosing(true);

              window.setTimeout(() => {
                setAccessClosing(false);
                setAccessOpen(false);
                setAccessStep('credentials');
                setVerificationCode('');
                setVerificationEmail('');
                setVerificationError('');
              }, 240);
            }}
            aria-label="Fermer"
          >
            ×
          </button>

          {isVerificationStep ? (
            <>
              <div className="access-eyebrow">
                <span className="eyebrow-line" />
                VÉRIFICATION
              </div>

              <h1>
                Vérifiez votre connexion.
              </h1>

              <p>
                Une nouvelle connexion a été détectée.
                Un code à 6 chiffres vient d’être envoyé
                à <strong>{verificationEmail}</strong>.
              </p>

              <form
                className="access-form"
                onSubmit={verifyEmailCode}
              >
                <label>
                  Code de vérification

                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(event) =>
                      setVerificationCode(
                        event.target.value
                          .replace(/\D/g, '')
                          .slice(0, 6)
                      )
                    }
                    placeholder="123456"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    required
                  />
                </label>

                {verificationError && (
                  <div className="access-error">
                    {verificationError}
                  </div>
                )}

                <button
                  type="submit"
                  className="access-submit"
                  disabled={
                    verificationLoading ||
                    verificationCode.length !== 6
                  }
                >
                  {verificationLoading
                    ? 'Vérification…'
                    : 'Vérifier ma connexion'}

                  {!verificationLoading && (
                    <span>→</span>
                  )}
                </button>
              </form>

              <div className="verification-note">
                Le code est valable 10 minutes.
                Si vous n’êtes pas à l’origine de cette connexion,
                vous pouvez simplement fermer cette fenêtre.
              </div>

              <button
                type="button"
                className="verification-back"
                onClick={() => {
                  setVerificationError('');
                  setVerificationCode('');
                  setAccessStep('credentials');
                }}
              >
                ← Retour
              </button>
            </>
          ) : (
            <>
              <div className="access-eyebrow">
                <span className="eyebrow-line" />
                ACCÈS PRIVÉ
              </div>

              <h1>
                Accédez au générateur.
              </h1>

              <p>
                Entrez l’adresse e-mail associée à votre accès
                ainsi que le code délivré par le Staff Resell Addict.
              </p>

              <form
                className="access-form"
                onSubmit={verifyAccess}
              >
                <label>
                  Adresse e-mail

                  <input
                    type="email"
                    value={accessEmail}
                    onChange={(event) =>
                      setAccessEmail(event.target.value)
                    }
                    placeholder="vous@exemple.com"
                    autoComplete="email"
                    required
                  />
                </label>

                <label>
                  Code d’accès

                  <input
                    type="text"
                    value={accessCode}
                    onChange={(event) =>
                      setAccessCode(
                        event.target.value.toUpperCase()
                      )
                    }
                    placeholder="RA-XXXX-XXXX-XXXX"
                    autoComplete="one-time-code"
                    spellCheck="false"
                    required
                  />
                </label>

                {accessError && (
                  <div className="access-error">
                    {accessError}
                  </div>
                )}

                <button
                  type="submit"
                  className="access-submit"
                  disabled={accessLoading}
                >
                  {accessLoading
                    ? 'Vérification…'
                    : 'Continuer'}

                  {!accessLoading && (
                    <span>→</span>
                  )}
                </button>
              </form>

              <div className="access-purchase">
                <span>
                  Pas encore de code ?
                </span>{' '}

                <button
                  type="button"
                  className="access-purchase-link"
                  onClick={() => {
                    setAccessOpen(false);
                    navigateWithTransition('pricing', 220);
                  }}
                >
                  Achète le ici
                </button>
              </div>

              <div className="access-note">
                Une vérification par e-mail est demandée lors d'une
                nouvelle connexion.
              </div>
            </>
          )}

        </div>
      </main>
    );
  }

  // =========================
  // HOME
  // =========================
  if (page === 'home') {
    return (
      <main
        className={`home-page ${
          pageNavigating
            ? 'page-navigating'
            : ''
        }`}
      >

        <nav className="home-nav">
          <div className="wordmark">
            RESELL ADDICT
          </div>

          <div className="nav-right">
            <span className="nav-status">
              Générateur de tickets
            </span>

            <button
              className="nav-pricing"
              onClick={() => navigateWithTransition('pricing', 220)}
            >
              <span>Tarifs</span>
              <span className="nav-link-indicator" aria-hidden="true" />
            </button>

            <span className="nav-divider" aria-hidden="true" />

            <button
              className="nav-faq"
              onClick={() => navigateWithTransition('faq', 220)}
            >
              <span>FAQ</span>
              <span className="nav-link-indicator" aria-hidden="true" />
            </button>

            <button
              className="nav-start"
              onClick={requestGeneratorAccess}
            >
              Commencer
            </button>
          </div>
        </nav>

        <section className="hero-section">

          <div className="hero-content">

            <div className="hero-eyebrow">
              <span className="eyebrow-line" />
              OUTIL RESSELL ADDICT
            </div>

            <h1 className="hero-title">
              Bienvenue sur le générateur de Resell Addict.
            </h1>

            <p className="hero-description">
              Créez et personnalisez des tickets et factures directement depuis votre navigateur.
              Choisissez un modèle, modifiez les informations et exportez votre document
              en quelques secondes.
            </p>

            <div className="hero-actions">

              <button
                className="primary-action"
                onClick={requestGeneratorAccess}
              >
                <span>
                  Ouvrir le générateur
                </span>

                <span className="action-arrow">
                  →
                </span>
              </button>

              <div className="discord-line">
                <span>
                  Pas encore sur notre serveur ?
                </span>

                <a
                  href="https://discord.gg/wnUWjvvt3r"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Rejoins dès maintenant.
                </a>
              </div>

            </div>
          </div>

          <div className="hero-visual">

            <div className="visual-caption">
              <span>01</span>
              <span>APERÇU</span>
            </div>

            <div className="ticket-scene">

              <div className="mock-ticket ticket-back">
                <div className="mock-ticket-lines">
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              </div>

              <div className="mock-ticket ticket-front">

                <div className="ticket-top">
                  <span>
                    RESELL ADDICT
                  </span>

                  <span>
                    01
                  </span>
                </div>

                <div className="ticket-divider" />

                <div className="ticket-center">
                  <strong>
                    TICKET
                  </strong>

                  <span>
                    Générateur
                  </span>
                </div>

                <div className="ticket-info">

                  <div>
                    <small>
                      DATE
                    </small>

                    <strong>
                      19/08/2026
                    </strong>
                  </div>

                  <div>
                    <small>
                      RÉFÉRENCE
                    </small>

                    <strong>
                      RA-24081
                    </strong>
                  </div>

                </div>

                <div className="ticket-bars">
                  {Array.from({
                    length: 18
                  }).map((_, i) => (
                    <i key={i} />
                  ))}
                </div>

                <div className="ticket-bottom">
                  RESELL ADDICT
                </div>

              </div>
            </div>

            <div className="visual-note">
              <span>SIMPLE</span>
              <span>RAPIDE</span>
              <span>DIRECT</span>
            </div>

          </div>

        </section>


        <footer className="home-footer">
          <span>RESELL ADDICT</span>
          <span>Générateur de tickets</span>
          <span>© 2026</span>
        </footer>

      </main>
    );
  }

  // =========================
  // TARIFS
  // =========================
  if (page === 'pricing') {
    return (
      <main
        className={`pricing-page ${
          pageNavigating
            ? 'page-navigating'
            : ''
        }`}
      >
        <nav className="pricing-nav">
          <button
            className="services-back"
            onClick={() =>
              navigateWithTransition(
                'home',
                220
              )
            }
          >
            ← Accueil
          </button>

          <div className="wordmark">
            RESELL ADDICT
          </div>

          <div className="services-nav-spacer" />
        </nav>

        <section className="pricing-content">
          <div className="pricing-heading">
            <div className="hero-eyebrow">
              <span className="eyebrow-line" />
              TARIFS
            </div>

            <h1>
              Un accès. Tous les générateurs.
            </h1>

            <p>
              Un paiement unique pour profiter de l'ensemble des outils Resell Addict.
            </p>
          </div>

          <div className="pricing-card">
            <div className="pricing-card-top">
              <div>
                <span className="pricing-label">
                  ACCÈS À VIE
                </span>

                <h2>
                  Accès illimité
                </h2>
              </div>

              <div className="pricing-price">
                <span>25</span>
                <small>€</small>
              </div>
            </div>

            <div className="pricing-divider" />

            <div className="pricing-benefits">
              <div>
                <span>✓</span>
                Générateur de tickets de caisse
              </div>

              <div>
                <span>✓</span>
                Générateur de factures
              </div>

              <div>
                <span>✓</span>
                Tous les modèles disponibles
              </div>

              <div>
                <span>✓</span>
                Export PDF
              </div>

              <div>
                <span>✓</span>
                Accès sans expiration
              </div>

              <div>
                <span>✓</span>
                Mises à jour futures
              </div>
            </div>

            <a
              className="pricing-buy-button"
              href="https://discord.gg/wnUWjvvt3r"
              target="_blank"
              rel="noopener noreferrer"
            >
              Acheter maintenant
              <span>→</span>
            </a>

            <div className="pricing-note">
              Paiement unique — aucun abonnement
            </div>
          </div>
        </section>

        <footer className="home-footer pricing-footer">
          <span>RESELL ADDICT</span>
          <span>Tarifs</span>
          <span>© 2026</span>
        </footer>
      </main>
    );
  }

  // =========================
  // FAQ
  // =========================
  if (page === 'faq') {
    return (
      <main
        className={`faq-page ${
          pageNavigating
            ? 'page-navigating'
            : ''
        }`}
      >
        <nav className="faq-nav">
          <button
            className="services-back"
            onClick={() =>
              navigateWithTransition(
                'home',
                220
              )
            }
          >
            ← Accueil
          </button>

          <div className="wordmark">
            RESELL ADDICT
          </div>

          <div className="services-nav-spacer" />
        </nav>

        <section className="faq-content">
          <div className="faq-heading">
            <div className="hero-eyebrow">
              <span className="eyebrow-line" />
              QUESTIONS FRÉQUENTES
            </div>

            <h1>
              Besoin d'aide ?
            </h1>

            <p>
              Retrouvez les réponses aux questions les plus fréquentes sur Resell Addict.
            </p>
          </div>

          <div className="faq-list">
            {faqItems.map((item, index) => {
              const isOpen = openFaq === index;

              return (
                <div
                  className={`faq-item ${isOpen ? 'is-open' : ''}`}
                  key={item.question}
                >
                  <button
                    type="button"
                    className="faq-question"
                    onClick={() =>
                      setOpenFaq(isOpen ? null : index)
                    }
                    aria-expanded={isOpen}
                  >
                    <span>{item.question}</span>
                    <span className="faq-icon">
                      {isOpen ? '−' : '+'}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="faq-answer">
                      <p>{item.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <footer className="home-footer faq-footer">
          <span>RESELL ADDICT</span>
          <span>FAQ</span>
          <span>© 2026</span>
        </footer>
      </main>
    );
  }

  // =========================
  // SERVICES
  // =========================
  if (page === 'services') {
    return (
      <main
        className={`services-page ${
          pageNavigating
            ? 'page-navigating'
            : ''
        }`}
      >

        <nav className="services-nav">

          <button
            className="services-back"
            onClick={() =>
              navigateWithTransition(
                'home',
                220
              )
            }
          >
            ← Accueil
          </button>

          <div className="wordmark">
            RESELL ADDICT
          </div>

          <div className="services-nav-spacer" />

        </nav>

        <section className="services-content">

          <div className="services-heading">

            <div className="hero-eyebrow">
              <span className="eyebrow-line" />
              NOS OUTILS
            </div>

            <h1>
              Choisissez un service.
            </h1>

            <p>
              Sélectionnez l'outil que vous souhaitez utiliser pour commencer.
            </p>

            {getAccessDurationLabel() && (
              <div className="access-duration">
                <span className="access-duration-dot" />
                <span>{getAccessDurationLabel()}</span>
              </div>
            )}

          </div>

          <div className="services-grid">

            {/* =========================
                TICKET DE CAISSE
            ========================= */}

            <button
              className="service-card"
              onClick={openTicketGenerator}
              disabled={serviceOpening}
            >

              <div className="service-card-top">

                <span className="service-number">
                  01
                </span>

                <span className="service-arrow">
                  ↗
                </span>

              </div>

              <div className="service-card-content">

                <div className="service-icon">
                  T
                </div>

                <h2>
                  Ticket de caisse
                </h2>

                <p>
                  Créez et personnalisez votre ticket de caisse directement depuis votre navigateur.
                </p>

              </div>

              <div className="service-card-bottom">
                <span>
                  OUVRIR L'OUTIL
                </span>

                <span>
                  →
                </span>
              </div>

            </button>

            {/* =========================
                FACTURES
            ========================= */}

            <button
              className="service-card"
              onClick={openInvoiceGenerator}
              disabled={serviceOpening}
            >

              <div className="service-card-top">

                <span className="service-number">
                  02
                </span>

                <span className="service-arrow">
                  ↗
                </span>

              </div>

              <div className="service-card-content">

                <div className="service-icon">
                  F
                </div>

                <h2>
                  Factures
                </h2>

                <p>
                  Créez et personnalisez vos factures directement depuis votre navigateur.
                </p>

              </div>

              <div className="service-card-bottom">

                <span>
                  OUVRIR L'OUTIL
                </span>

                <span>
                  →
                </span>

              </div>

            </button>

          </div>

        </section>

      </main>
    );
  }

  // =========================
  // FACTURES
  // =========================
  if (page === 'invoice-editor') {
    return (
      <main
      className={`editor-page ${
        pageNavigating
          ? 'page-navigating'
          : ''
      } ${
        generatorEntering
          ? 'generator-entry-fade'
          : ''
      }`}
    >

        <header className="editor-header">

          <div className="editor-brand">

            <div className="editor-wordmark">
              RESELL ADDICT
            </div>

            <span className="editor-separator" />

            <span className="editor-title-small">
              Générateur de facture
            </span>

          </div>

          <button
            className="editor-home-button"
            onClick={() =>
              navigateWithTransition(
                'services',
                220
              )
            }
          >
            <span>←</span>
            Services
          </button>

        </header>

        <section className="editor-toolbar">

          <div className="template-selector-wrap">

            <span className="control-label">
              MODÈLE
            </span>

            <div
              className="template-dropdown"
              ref={invoiceMenuRef}
            >


              {getNewTemplateCount('invoice', invoiceTemplateIds) > 0 && (

                <span className="template-new-count">

                  +{getNewTemplateCount('invoice', invoiceTemplateIds)}

                </span>

              )}


              <button
                type="button"
                className={`template-dropdown-trigger ${
                  invoiceMenuOpen
                    ? 'is-open'
                    : ''
                }`}
                onClick={() =>
                  setInvoiceMenuOpen(
                    (value) => !value
                  )
                }
                aria-expanded={invoiceMenuOpen}
              >

                <span>
                  {invoiceTemplateMeta?.name ||
                    selectedInvoiceTemplate}
                </span>

                <span className="template-chevron">
                  ⌄
                </span>

              </button>

              {invoiceMenuOpen && (
                <div className="template-dropdown-menu">

                  {invoiceTemplateIds.map(
                    (id) => (
                      <button
                        type="button"
                        key={id}
                        className={`template-option ${
                          id === selectedInvoiceTemplate
                            ? 'selected'
                            : ''
                        }`}
                        onClick={() =>
                          handleInvoiceTemplateChange(
                            id
                          )
                        }
                      >

                        <span className="template-option-name">


                          {invoiceTemplates[id]?.name ||


                            id}


                        </span>



                        <span className="template-option-meta">


                          {isTemplateNewForCurrentUser('invoice', id) && (


                            <span className="template-new-pill">


                              NOUVEAU


                            </span>


                          )}



                          {id === selectedInvoiceTemplate && (


                            <span className="template-check">


                              ✓


                            </span>


                          )}


                        </span>

                      </button>
                    )
                  )}

                </div>
              )}

            </div>

            <div className="editor-toolbar-actions">
              <button
                type="button"
                className="suggest-brand-button"
                onClick={() =>
                  openBrandSuggestion('invoice')
                }
              >
                <span className="suggest-brand-plus">+</span>
                <span>Sugg&eacute;rer une marque</span>
              </button>

              <button
                type="button"
                className="report-problem-button"
                onClick={() =>
                  openProblemReport('invoice')
                }
              >
                <span className="report-problem-icon">!</span>
                <span>Signaler un probl&egrave;me</span>
              </button>
            </div>

          </div>

          <div className="editor-hint">
            <span className="hint-circle" />
            Aperçu de votre facture.
          </div>

        </section>

        <section className="editor-workspace">

          <div className="preview-panel">

            <div className="preview-header">

              <div>

                <span className="preview-label">
                  APERÇU DU DOCUMENT
                </span>

                <h2>
                  {invoiceTemplateMeta?.name ||
                    'Facture'}
                </h2>

              </div>

              <span className="edit-status">
                FACTURE
              </span>

            </div>

            <div className="preview-stage">

              <div
                ref={invoicePreviewRef}
                className="preview-ref"
              >

                <div
                  className={
                    invoiceAnimating
                      ? 'template-transition'
                      : ''
                  }
                >

                  {invoiceTemplate ? (
                    <InvoicePreview
                      template={invoiceTemplate}
                      formData={invoiceFormData}
                      onTextChange={({ key, text }) => {
                        setInvoiceFormData((previous) => ({
                          ...previous,
                          [key]: text
                        }));
                      }}
                    />
                  ) : (
                    <div className="template-loading">
                      Chargement du modèle…
                    </div>
                  )}

                </div>

              </div>

            </div>

          </div>

          <aside className="editor-sidebar">

            <div className="sidebar-section">

              <span className="sidebar-label">
                MODÈLE ACTUEL
              </span>

              <h3>
                {invoiceTemplateMeta?.name ||
                  'Facture'}
              </h3>

              <p>
                Aperçu de votre facture Resell Addict.
              </p>

            </div>

            <div className="sidebar-divider" />

            <div className="sidebar-section">

              <span className="sidebar-label">
                FACTURE
              </span>

              <div className="instruction">

                <span className="instruction-number">
                  01
                </span>

                <span>
                  Sélectionnez un modèle de facture.
                </span>

              </div>

              <div className="instruction">

                <span className="instruction-number">
                  02
                </span>

                <span>
                  Consultez l'aperçu du document.
                </span>

              </div>

              <div className="instruction">

                <span className="instruction-number">
                  03
                </span>

                <span>
                  Exportez votre document.
                </span>

              </div>

            </div>

            <div className="sidebar-export">

              {invoiceTemplate ? (
                <PDFExportButton
                  previewRef={invoicePreviewRef}
                  template={invoiceTemplate}
                  formData={invoiceFormData}
                  fileName={`facture-${selectedInvoiceTemplate}.pdf`}
                />
              ) : (
                <button
                  type="button"
                  className="export-button"
                  disabled
                >
                  Chargement…
                </button>
              )}

            </div>

          </aside>

        </section>

      
        {brandSuggestionOpen && (
          <div
            className="brand-suggest-backdrop"
            onMouseDown={(event) => {
              if (
                event.target === event.currentTarget &&
                !brandSuggestionSending
              ) {
                closeBrandSuggestion();
              }
            }}
          >
            <div
              className="brand-suggest-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="brand-suggest-title"
            >
              <button
                type="button"
                className="brand-suggest-close"
                onClick={closeBrandSuggestion}
                disabled={brandSuggestionSending}
                aria-label="Fermer"
              >
                &times;
              </button>

              <span className="brand-suggest-eyebrow">
                SUGGESTION
              </span>

              <h3 id="brand-suggest-title">
                Sugg&eacute;rer une marque
              </h3>

              <p>
                La marque sera envoy&eacute;e directement au staff Resell Addict.
              </p>

              <form onSubmit={submitBrandSuggestion}>
                <label className="brand-suggest-label">
                  Nom de la marque
                  <input
                    type="text"
                    value={brandSuggestionName}
                    onChange={(event) => {
                      setBrandSuggestionName(
                        event.target.value.slice(0, 80)
                      );
                      if (brandSuggestionMessage) {
                        setBrandSuggestionMessage('');
                        setBrandSuggestionIsError(false);
                      }
                    }}
                    placeholder="Ex. Balenciaga"
                    maxLength={80}
                    autoFocus
                    disabled={brandSuggestionSending}
                  />
                </label>

                <div className="brand-suggest-context">
                  <span>Mod&egrave;le actuel</span>
                  <strong>{getSuggestedCurrentTemplate()}</strong>
                </div>

                {brandSuggestionMessage && (
                  <div
                    className={`brand-suggest-message ${
                      brandSuggestionIsError
                        ? 'is-error'
                        : 'is-success'
                    }`}
                  >
                    {brandSuggestionMessage}
                  </div>
                )}

                <div className="brand-suggest-actions">
                  <button
                    type="button"
                    className="brand-suggest-cancel"
                    onClick={closeBrandSuggestion}
                    disabled={brandSuggestionSending}
                  >
                    Annuler
                  </button>

                  <button
                    type="submit"
                    className="brand-suggest-submit"
                    disabled={
                      brandSuggestionSending ||
                      brandSuggestionName.trim().length < 2
                    }
                  >
                    {brandSuggestionSending
                      ? 'Envoi...'
                      : 'Envoyer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {problemReportOpen && (
          <div
            className="brand-suggest-backdrop"
            onMouseDown={(event) => {
              if (
                event.target === event.currentTarget &&
                !problemReportSending
              ) {
                closeProblemReport();
              }
            }}
          >
            <div
              className="brand-suggest-modal problem-report-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="problem-report-title"
            >
              <button
                type="button"
                className="brand-suggest-close"
                onClick={closeProblemReport}
                disabled={problemReportSending}
                aria-label="Fermer"
              >
                &times;
              </button>

              <span className="brand-suggest-eyebrow problem-report-eyebrow">
                SUPPORT
              </span>

              <h3 id="problem-report-title">
                Signaler un probl&egrave;me
              </h3>

              <p>
                D&eacute;cris le probl&egrave;me rencontr&eacute; et ajoute une capture si cela peut aider le staff.
              </p>

              <form onSubmit={submitProblemReport}>
                <label className="brand-suggest-label problem-report-label">
                  Probl&egrave;me rencontr&eacute;
                  <textarea
                    value={problemReportText}
                    onChange={(event) => {
                      setProblemReportText(event.target.value.slice(0, 3000));
                      if (problemReportMessage) {
                        setProblemReportMessage('');
                        setProblemReportIsError(false);
                      }
                    }}
                    placeholder="Explique ce qui ne fonctionne pas..."
                    maxLength={3000}
                    rows={6}
                    autoFocus
                    disabled={problemReportSending}
                  />
                </label>

                <div className="problem-report-attachment">
                  <span className="problem-report-attachment-label">
                    Pi&egrave;ce jointe <span>(facultatif)</span>
                  </span>

                  <label className="problem-report-file-picker">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      onChange={handleProblemReportFile}
                      disabled={problemReportSending}
                    />
                    <span className="problem-report-file-button">
                      Ajouter une capture
                    </span>
                    <span className="problem-report-file-hint">
                      PNG, JPG, WebP ou GIF — 8 Mo max
                    </span>
                  </label>

                  {problemReportFile && (
                    <div className="problem-report-file-selected">
                      <span title={problemReportFile.name}>
                        {problemReportFile.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => setProblemReportFile(null)}
                        disabled={problemReportSending}
                      >
                        Retirer
                      </button>
                    </div>
                  )}
                </div>

                <div className="brand-suggest-context">
                  <span>Mod&egrave;le actuel</span>
                  <strong>{getProblemReportCurrentTemplate()}</strong>
                </div>

                {problemReportMessage && (
                  <div
                    className={`brand-suggest-message ${
                      problemReportIsError
                        ? 'is-error'
                        : 'is-success'
                    }`}
                  >
                    {problemReportMessage}
                  </div>
                )}

                <div className="brand-suggest-actions">
                  <button
                    type="button"
                    className="brand-suggest-cancel"
                    onClick={closeProblemReport}
                    disabled={problemReportSending}
                  >
                    Annuler
                  </button>

                  <button
                    type="submit"
                    className="brand-suggest-submit"
                    disabled={
                      problemReportSending ||
                      problemReportText.trim().length < 5
                    }
                  >
                    {problemReportSending
                      ? 'Envoi...'
                      : 'Envoyer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}


      </main>
    );
  }

  // =========================
  // TICKET EDITOR
  // =========================
  return (
    <main
      className={`editor-page ${
        pageNavigating
          ? 'page-navigating'
          : ''
      } ${
        generatorEntering
          ? 'generator-entry-fade'
          : ''
      }`}
    >

      <header className="editor-header">

        <div className="editor-brand">

          <div className="editor-wordmark">
            RESELL ADDICT
          </div>

          <span className="editor-separator" />

          <span className="editor-title-small">
            Générateur de ticket
          </span>

        </div>

        <button
          className="editor-home-button"
          onClick={() =>
              navigateWithTransition(
                'services',
                220
              )
            }
        >
          <span>←</span>
          Services
        </button>

      </header>

      <section className="editor-toolbar">

        <div className="template-selector-wrap">

          <span className="control-label">
            MODÈLE
          </span>

          <div
            className="template-dropdown"
            ref={templateMenuRef}
          >


            {getNewTemplateCount('ticket', templateIds) > 0 && (

              <span className="template-new-count">

                +{getNewTemplateCount('ticket', templateIds)}

              </span>

            )}


            <button
              type="button"
              className={`template-dropdown-trigger ${
                templateMenuOpen
                  ? 'is-open'
                  : ''
              }`}
              onClick={() =>
                setTemplateMenuOpen(
                  (value) => !value
                )
              }
              aria-expanded={templateMenuOpen}
            >

              <span>
                {templateMeta?.name ||
                  selectedTemplate}
              </span>

              <span className="template-chevron">
                ⌄
              </span>

            </button>

            {templateMenuOpen && (
              <div className="template-dropdown-menu">

                {templateIds.map((id) => (
                  <button
                    type="button"
                    key={id}
                    className={`template-option ${
                      id === selectedTemplate
                        ? 'selected'
                        : ''
                    }`}
                    onClick={() =>
                      handleTemplateChange(id)
                    }
                  >

                    <span className="template-option-name">


                      {templates[id]?.name ||


                        id}


                    </span>



                    <span className="template-option-meta">


                      {isTemplateNewForCurrentUser('ticket', id) && (


                        <span className="template-new-pill">


                          NOUVEAU


                        </span>


                      )}



                      {id === selectedTemplate && (


                        <span className="template-check">


                          ✓


                        </span>


                      )}


                    </span>

                  </button>
                ))}

              </div>
            )}

          </div>

          <div className="editor-toolbar-actions">
            <button
              type="button"
              className="suggest-brand-button"
              onClick={() =>
                openBrandSuggestion('ticket')
              }
            >
              <span className="suggest-brand-plus">+</span>
              <span>Sugg&eacute;rer une marque</span>
            </button>

            <button
              type="button"
              className="report-problem-button"
              onClick={() =>
                openProblemReport('ticket')
              }
            >
              <span className="report-problem-icon">!</span>
              <span>Signaler un probl&egrave;me</span>
            </button>
          </div>

        </div>

        <div className="editor-hint">
          <span className="hint-circle" />
          Double-cliquez sur un texte pour le modifier.
        </div>

      </section>

      <section className="editor-workspace">

        <div className="preview-panel">

          <div className="preview-header">

            <div>

              <span className="preview-label">
                APERÇU DU DOCUMENT
              </span>

              <h2>
                {templateMeta?.name ||
                  'Template'}
              </h2>

            </div>

            <span className="edit-status">
              ÉDITION DIRECTE
            </span>

          </div>

          <div className="preview-stage">

            <div
              ref={previewRef}
              className="preview-ref"
            >

              <div
                className={
                  templateAnimating
                    ? 'template-transition'
                    : ''
                }
              >
                {template ? (
                  <ReceiptPreview
                    template={template}
                    formData={formData}
                    onChange={setFormData}
                    animateTemplate={templateAnimating}
                  />
                ) : (
                  <div className="template-loading">
                    Chargement du modèle…
                  </div>
                )}
              </div>

            </div>

          </div>

        </div>

        <aside className="editor-sidebar">

          <div className="sidebar-section">

            <span className="sidebar-label">
              MODÈLE ACTUEL
            </span>

            <h3>
              {templateMeta?.name ||
                'Template'}
            </h3>

            <p>
              Modifiez directement les informations affichées sur le ticket.
            </p>

          </div>

          <div className="sidebar-divider" />

          <div className="sidebar-section">

            <span className="sidebar-label">
              MODIFICATION
            </span>

            <div className="instruction">

              <span className="instruction-number">
                01
              </span>

              <span>
                Double-cliquez sur le texte à modifier.
              </span>

            </div>

            <div className="instruction">

              <span className="instruction-number">
                02
              </span>

              <span>
                Remplacez son contenu.
              </span>

            </div>

            <div className="instruction">

              <span className="instruction-number">
                03
              </span>

              <span>
                Cliquez ailleurs pour enregistrer.
              </span>

            </div>

          </div>

          <div className="sidebar-export">

            {template ? (
              <PDFExportButton
                previewRef={previewRef}
                template={template}
                formData={formData}
                fileName={`ticket-${selectedTemplate}.pdf`}
              />
            ) : (
              <button
                type="button"
                className="export-button"
                disabled
              >
                Chargement…
              </button>
            )}

          </div>

        </aside>

      </section>

    
        {brandSuggestionOpen && (
          <div
            className="brand-suggest-backdrop"
            onMouseDown={(event) => {
              if (
                event.target === event.currentTarget &&
                !brandSuggestionSending
              ) {
                closeBrandSuggestion();
              }
            }}
          >
            <div
              className="brand-suggest-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="brand-suggest-title"
            >
              <button
                type="button"
                className="brand-suggest-close"
                onClick={closeBrandSuggestion}
                disabled={brandSuggestionSending}
                aria-label="Fermer"
              >
                &times;
              </button>

              <span className="brand-suggest-eyebrow">
                SUGGESTION
              </span>

              <h3 id="brand-suggest-title">
                Sugg&eacute;rer une marque
              </h3>

              <p>
                La marque sera envoy&eacute;e directement au staff Resell Addict.
              </p>

              <form onSubmit={submitBrandSuggestion}>
                <label className="brand-suggest-label">
                  Nom de la marque
                  <input
                    type="text"
                    value={brandSuggestionName}
                    onChange={(event) => {
                      setBrandSuggestionName(
                        event.target.value.slice(0, 80)
                      );
                      if (brandSuggestionMessage) {
                        setBrandSuggestionMessage('');
                        setBrandSuggestionIsError(false);
                      }
                    }}
                    placeholder="Ex. Balenciaga"
                    maxLength={80}
                    autoFocus
                    disabled={brandSuggestionSending}
                  />
                </label>

                <div className="brand-suggest-context">
                  <span>Mod&egrave;le actuel</span>
                  <strong>{getSuggestedCurrentTemplate()}</strong>
                </div>

                {brandSuggestionMessage && (
                  <div
                    className={`brand-suggest-message ${
                      brandSuggestionIsError
                        ? 'is-error'
                        : 'is-success'
                    }`}
                  >
                    {brandSuggestionMessage}
                  </div>
                )}

                <div className="brand-suggest-actions">
                  <button
                    type="button"
                    className="brand-suggest-cancel"
                    onClick={closeBrandSuggestion}
                    disabled={brandSuggestionSending}
                  >
                    Annuler
                  </button>

                  <button
                    type="submit"
                    className="brand-suggest-submit"
                    disabled={
                      brandSuggestionSending ||
                      brandSuggestionName.trim().length < 2
                    }
                  >
                    {brandSuggestionSending
                      ? 'Envoi...'
                      : 'Envoyer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {problemReportOpen && (
          <div
            className="brand-suggest-backdrop"
            onMouseDown={(event) => {
              if (
                event.target === event.currentTarget &&
                !problemReportSending
              ) {
                closeProblemReport();
              }
            }}
          >
            <div
              className="brand-suggest-modal problem-report-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="problem-report-title"
            >
              <button
                type="button"
                className="brand-suggest-close"
                onClick={closeProblemReport}
                disabled={problemReportSending}
                aria-label="Fermer"
              >
                &times;
              </button>

              <span className="brand-suggest-eyebrow problem-report-eyebrow">
                SUPPORT
              </span>

              <h3 id="problem-report-title">
                Signaler un probl&egrave;me
              </h3>

              <p>
                D&eacute;cris le probl&egrave;me rencontr&eacute; et ajoute une capture si cela peut aider le staff.
              </p>

              <form onSubmit={submitProblemReport}>
                <label className="brand-suggest-label problem-report-label">
                  Probl&egrave;me rencontr&eacute;
                  <textarea
                    value={problemReportText}
                    onChange={(event) => {
                      setProblemReportText(event.target.value.slice(0, 3000));
                      if (problemReportMessage) {
                        setProblemReportMessage('');
                        setProblemReportIsError(false);
                      }
                    }}
                    placeholder="Explique ce qui ne fonctionne pas..."
                    maxLength={3000}
                    rows={6}
                    autoFocus
                    disabled={problemReportSending}
                  />
                </label>

                <div className="problem-report-attachment">
                  <span className="problem-report-attachment-label">
                    Pi&egrave;ce jointe <span>(facultatif)</span>
                  </span>

                  <label className="problem-report-file-picker">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      onChange={handleProblemReportFile}
                      disabled={problemReportSending}
                    />
                    <span className="problem-report-file-button">
                      Ajouter une capture
                    </span>
                    <span className="problem-report-file-hint">
                      PNG, JPG, WebP ou GIF — 8 Mo max
                    </span>
                  </label>

                  {problemReportFile && (
                    <div className="problem-report-file-selected">
                      <span title={problemReportFile.name}>
                        {problemReportFile.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => setProblemReportFile(null)}
                        disabled={problemReportSending}
                      >
                        Retirer
                      </button>
                    </div>
                  )}
                </div>

                <div className="brand-suggest-context">
                  <span>Mod&egrave;le actuel</span>
                  <strong>{getProblemReportCurrentTemplate()}</strong>
                </div>

                {problemReportMessage && (
                  <div
                    className={`brand-suggest-message ${
                      problemReportIsError
                        ? 'is-error'
                        : 'is-success'
                    }`}
                  >
                    {problemReportMessage}
                  </div>
                )}

                <div className="brand-suggest-actions">
                  <button
                    type="button"
                    className="brand-suggest-cancel"
                    onClick={closeProblemReport}
                    disabled={problemReportSending}
                  >
                    Annuler
                  </button>

                  <button
                    type="submit"
                    className="brand-suggest-submit"
                    disabled={
                      problemReportSending ||
                      problemReportText.trim().length < 5
                    }
                  >
                    {problemReportSending
                      ? 'Envoi...'
                      : 'Envoyer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}


    </main>
  );
}

export default App;